import React, { useEffect, useState } from "react";
import "./busTerms.css";
import usePageMeta from "../../../hooks/usePageMeta";

const busPrivacySections = [
  {
    number: 1,
    title: "Information We Collect",
    paragraphs: [
      "When you use our bus booking service, we may collect information such as:",
    ],
    bullets: [
      "Passenger's full name",
      "Age",
      "Gender",
      "Mobile number",
      "Email address",
      "Travel date",
      "Boarding point",
      "Dropping point",
      "Bus operator",
      "Route details",
      "Seat preference and seat number",
      "Booking reference or ticket details",
      "Payment and transaction information",
      "Identification details where required by the bus operator or applicable regulations",
    ],
    after: [
      "We may also collect technical information such as IP address, browser type, device information and website usage data.",
    ],
  },

  {
    number: 2,
    title: "How We Use Your Information",
    paragraphs: ["Your information may be used to:"],
    bullets: [
      "Search and display available bus services",
      "Process bus ticket bookings",
      "Generate and send booking confirmations",
      "Issue bus tickets",
      "Process payments",
      "Process cancellations and refunds",
      "Provide customer support",
      "Send travel and booking updates",
      "Contact you regarding your reservation",
      "Assist with rescheduling or modification requests",
      "Prevent fraudulent or unauthorized transactions",
      "Comply with applicable legal and regulatory requirements",
      "Improve our website and booking services",
    ],
  },

  {
    number: 3,
    title: "Sharing of Information",
    paragraphs: [
      "To complete your bus booking, MakeBeeTrip may share necessary passenger and booking information with:",
    ],
    bullets: [
      "Bus operators",
      "Bus ticketing and reservation platforms",
      "Transport service providers",
      "Payment gateways and financial service providers",
      "Technology and booking service providers",
      "Customer support partners",
      "Government or regulatory authorities where legally required",
    ],
    after: [
      "Only information reasonably necessary to provide the requested service will be shared.",
    ],
  },

  {
    number: 4,
    title: "Payment Information",
    paragraphs: [
      "Payments may be processed through third-party payment gateways or financial service providers.",
      "MakeBeeTrip generally does not store complete debit card, credit card, CVV, UPI PIN or internet banking passwords on its own servers.",
      "Payment information may be processed directly by the applicable payment provider according to its own privacy policy and security practices.",
    ],
  },

  {
    number: 5,
    title: "Bus Operator Policies",
    paragraphs: [
      "Bus bookings may be subject to the terms, conditions, cancellation rules, refund policies and privacy practices of the respective bus operator or booking service provider.",
      "MakeBeeTrip is not responsible for the independent privacy practices of third-party bus operators or service providers.",
    ],
  },

  {
    number: 6,
    title: "Cookies",
    paragraphs: [
      "Our website may use cookies and similar technologies to:",
    ],
    bullets: [
      "Improve website functionality",
      "Remember user preferences",
      "Analyse website traffic",
      "Improve the booking experience",
      "Understand how visitors use our website",
      "Support marketing and advertising activities where applicable",
    ],
    after: [
      "You can manage cookies through your browser settings.",
    ],
  },

  {
    number: 7,
    title: "Data Security",
    paragraphs: [
      "We take reasonable technical and organizational measures to protect your personal information against unauthorized access, misuse, alteration, disclosure or destruction.",
      "However, no online transmission or electronic storage system can be guaranteed to be completely secure.",
    ],
  },

  {
    number: 8,
    title: "Data Retention",
    paragraphs: [
      "We may retain your information for as long as reasonably necessary to:",
    ],
    bullets: [
      "Complete your booking",
      "Provide customer support",
      "Process cancellations and refunds",
      "Maintain transaction and booking records",
      "Comply with legal and regulatory requirements",
      "Resolve disputes",
      "Prevent fraud and misuse",
    ],
    after: [
      "When information is no longer required, it may be securely deleted, anonymized or disposed of, subject to applicable legal requirements.",
    ],
  },

  {
    number: 9,
    title: "Marketing Communications",
    paragraphs: [
      "With your consent or where permitted by applicable law, we may send you promotional communications relating to:",
    ],
    bullets: [
      "Bus booking offers",
      "Travel packages",
      "Flight bookings",
      "Hotel bookings",
      "Holiday packages",
      "Cab services",
      "Visa services",
      "Other MakeBeeTrip travel services",
    ],
    after: [
      "You may opt out of promotional communications at any time.",
      "Service-related communications, such as booking confirmations, payment updates, ticket information and cancellation notices, may still be sent when necessary.",
    ],
  },

  {
    number: 10,
    title: "Your Privacy Rights",
    paragraphs: [
      "Subject to applicable law, you may request to:",
    ],
    bullets: [
      "Access your personal information",
      "Correct inaccurate information",
      "Request deletion of information where legally permitted",
      "Withdraw consent where applicable",
      "Opt out of promotional communications",
      "Raise a privacy-related complaint",
    ],
    after: [
      "For such requests, please contact us using the details below.",
    ],
  },

  {
    number: 11,
    title: "Third-Party Websites",
    paragraphs: [
      "Our website may contain links or integrations with third-party websites, booking platforms, payment gateways, bus operators and other service providers.",
      "MakeBeeTrip does not control the privacy policies or practices of these third parties.",
      "We recommend reviewing their respective privacy policies before submitting information directly to them.",
    ],
  },

  {
    number: 12,
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "MakeBeeTrip may update this Privacy Policy from time to time to reflect changes in our services, technology, business practices or applicable laws.",
      "Any updated Privacy Policy will be published on this page with a revised effective date.",
    ],
  },

  {
    number: 13,
    title: "Contact Us",
    paragraphs: [
      "For any questions, concerns or requests regarding this Privacy Policy or your personal information, please contact:",
    ],
    contact: true,
  },

  {
    number: 14,
    title: "Consent",
    paragraphs: [
      "By using the MakeBeeTrip website and submitting your information for a bus booking or enquiry, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use and processing of your information as described above, subject to applicable law.",
    ],
  },
];

function renderParagraph(text, sectionNumber) {
  if (sectionNumber === 4 && text.includes("generally does not store")) {
    const parts = text.split("generally does not store");

    return (
      <>
        {parts[0]}
        <strong>generally does not store</strong>
        {parts[1]}
      </>
    );
  }

  if (sectionNumber === 5 && text.includes("not responsible")) {
    const parts = text.split("not responsible");

    return (
      <>
        {parts[0]}
        <strong>not responsible</strong>
        {parts[1]}
      </>
    );
  }

  if (sectionNumber === 14) {
    const phrase =
      "acknowledge that you have read and understood this Privacy Policy";

    if (text.includes(phrase)) {
      const parts = text.split(phrase);

      return (
        <>
          {parts[0]}
          <strong>{phrase}</strong>
          {parts[1]}
        </>
      );
    }
  }

  return text;
}

function BusPrivacySection({ section }) {
  return (
    <section
      id={`bus-section-${section.number}`}
      className="mbt-bus-section"
    >
      <div className="mbt-bus-section__heading">
        <span className="mbt-bus-section__number">
          {String(section.number).padStart(2, "0")}
        </span>

        <h2>{section.title}</h2>
      </div>

      <div className="mbt-bus-section__content">
        {section.paragraphs?.map((paragraph, index) => (
          <p key={`paragraph-${section.number}-${index}`}>
            {renderParagraph(paragraph, section.number)}
          </p>
        ))}

        {section.bullets && (
          <ul>
            {section.bullets.map((bullet, index) => (
              <li key={`bullet-${section.number}-${index}`}>
                {bullet}
              </li>
            ))}
          </ul>
        )}

        {section.after?.map((paragraph, index) => (
          <p key={`after-${section.number}-${index}`}>
            {paragraph}
          </p>
        ))}

        {section.contact && (
          <div className="mbt-bus-contact-card">
            <p>
              <strong>MakeBeeTrip</strong>
            </p>

            <p>
              <strong>Website:</strong>{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                makebeetrip.com
              </a>
            </p>

            <p>
              <strong>Mobile:</strong>{" "}
              <a href="tel:+919355471999">+91 93554 71999</a>
            </p>

            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export default function Bus() {
  usePageMeta(
    "Bus Booking Privacy Policy | MakeBeeTrip",
    "Read the MakeBeeTrip Privacy Policy for bus booking services, including information collection, payments, data sharing, security, retention and privacy rights.",
    "bus booking privacy policy, MakeBeeTrip bus privacy, bus booking data privacy, passenger information"
  );

  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress =
        documentHeight > 0
          ? (scrollTop / documentHeight) * 100
          : 0;

      setScrollProgress(Math.min(progress, 100));
      setShowScrollTop(scrollTop > 500);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <main className="mbt-bus-page">
      {/* Scroll Progress */}
      <div
        className="mbt-bus-scroll-indicator"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Sticky Navigation */}
      <nav className="mbt-bus-sticky-nav">
        <div className="mbt-bus-nav-container">
          <button
            type="button"
            className="mbt-bus-back-button"
            onClick={() => window.history.back()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>

            Back
          </button>

          <span className="mbt-bus-nav-label">
            Bus Booking Privacy Policy
          </span>
        </div>
      </nav>

      {/* Hero */}
      <header className="mbt-bus-hero">
        <div className="mbt-bus-hero__container">
          {/* Bus Icon */}
          <div
            className="mbt-bus-icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 16.5V6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v10" />
              <path d="M5 13h14" />
              <path d="M7.5 7h9" />
              <path d="M8 10h2" />
              <path d="M14 10h2" />
              <path d="M5 16.5h14" />
              <path d="M7 16.5v2" />
              <path d="M17 16.5v2" />
              <circle cx="8" cy="18.5" r="1.5" />
              <circle cx="16" cy="18.5" r="1.5" />
            </svg>
          </div>

          <span className="mbt-bus-eyebrow">
            SERVICE-SPECIFIC PRIVACY POLICY
          </span>

          <h1>Bus Booking Privacy Policy</h1>

          <p className="mbt-bus-hero__description">
            Privacy terms applicable to information collected, processed and
            shared when searching for, enquiring about or booking bus tickets
            through MakeBeeTrip Private Limited.
          </p>

          <div className="mbt-bus-updated-badge">
            Effective Date: 29 September 2026
          </div>
        </div>
      </header>

      {/* Main Document */}
      <div className="mbt-bus-content">
        <article className="mbt-bus-card">
          {/* Introduction */}
          <div className="mbt-bus-intro">
            <p>
              At <strong>MakeBeeTrip</strong>, we respect your privacy and are
              committed to protecting the personal information you provide
              while using our website and bus booking services.
            </p>

            <p>
              This Privacy Policy explains how we collect, use, process, store
              and share information when you search for, enquire about or book
              bus tickets through our website.
            </p>
          </div>

          {/* Quick Navigation */}
          <nav
            className="mbt-bus-quick-nav"
            aria-label="Bus booking privacy policy sections"
          >
            <div className="mbt-bus-quick-nav__header">
              <span className="mbt-bus-quick-nav__eyebrow">
                QUICK NAVIGATION
              </span>

              <h2>Bus Booking Privacy Policy</h2>

              <p>
                Jump directly to any section of the Bus Booking Privacy Policy.
              </p>
            </div>

            <div className="mbt-bus-quick-nav__links">
              {busPrivacySections.map((section) => (
                <a
                  key={section.number}
                  href={`#bus-section-${section.number}`}
                >
                  <span>
                    {String(section.number).padStart(2, "0")}
                  </span>

                  {section.title}
                </a>
              ))}

              <a href="#bus-important-notice">
                <span>!</span>
                Important Notice
              </a>
            </div>
          </nav>

          {/* Sections */}
          <div className="mbt-bus-sections">
            {busPrivacySections.map((section, index) => (
              <React.Fragment key={section.number}>
                <BusPrivacySection section={section} />

                {index < busPrivacySections.length - 1 && (
                  <hr className="mbt-bus-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          <hr className="mbt-bus-divider" />

          {/* Important Notice */}
          <section
            id="bus-important-notice"
            className="mbt-bus-important-notice"
          >
            <div
              className="mbt-bus-important-notice__icon"
              aria-hidden="true"
            >
              !
            </div>

            <div>
              <span>IMPORTANT NOTICE</span>

              <p>
                <strong>
                  Bus bookings may involve third-party bus operators,
                  ticketing platforms, payment providers and other service
                  providers. Their own terms, privacy policies and security
                  practices may also apply to the relevant booking or service.
                </strong>
              </p>
            </div>
          </section>
        </article>
      </div>

      {/* Footer */}
      <footer className="mbt-bus-footer">
        <div className="mbt-bus-footer__inner">
          <p className="mbt-bus-footer__tagline">
            MakeBeeTrip – Your Journey,{" "}
            <span>Our Commitment</span>.
          </p>

          <div className="mbt-bus-footer__contact">
            <a href="tel:+919355471999">
              +91 93554 71999
            </a>

            <span aria-hidden="true">•</span>

            <a href="mailto:Holidays@makebeetrip.com">
              Holidays@makebeetrip.com
            </a>

            <span aria-hidden="true">•</span>

            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              makebeetrip.com
            </a>
          </div>

          <p className="mbt-bus-footer__copyright">
            © 2026 MakeBeeTrip Private Limited. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Scroll To Top */}
      {showScrollTop && (
        <button
          type="button"
          className="mbt-bus-scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </main>
  );
}